/**
 * Sync Manager for Enhanced Segmentation Agent v2.0
 * 
 * Fixes frontend sync issues by ensuring WebSocket messages properly
 * invalidate and refresh IndexedDB state after backend processing.
 * 
 * Author: Enhanced Segmentation Agent v2.0
 * Date: July 25, 2025
 */

import { useWebSocketStore } from '../store/websocketStore'
import { useBooksStore } from '../store/booksStore'
import { useSegmentationStore } from '../store/segmentationStore'
import { dbStorage } from './indexedDBStorage'

export interface SyncMessage {
  type: 'witness_updated' | 'segmentation_complete' | 'edit_operation_complete' | 'alignment_complete'
  witness_id: string
  chapter_id: string
  book_id: string
  timestamp: number
  version: number
  data?: any
}

export interface IndexedDBVersion {
  witness_id: string
  version: number
  last_updated: number
}

class SyncManager {
  private dbStorage = dbStorage
  private versionCache = new Map<string, number>()
  private retryQueue: Array<{witness_id: string, retries: number, timestamp: number}> = []
  private maxRetries = 3
  private retryDelay = 1000

  constructor() {
    this.setupWebSocketListeners()
    this.startRetryLoop()
  }

  /**
   * Setup WebSocket listeners for sync messages
   */
  private setupWebSocketListeners() {
    // Listen for witness processing updates
    window.addEventListener('websocket-message-processing', (event: any) => {
      const message = event.detail
      if (message.type === 'witness_updated' && message.data) {
        this.handleWitnessUpdate(message.data)
      }
    })

    // Listen for edit session updates
    window.addEventListener('websocket-message-edit', (event: any) => {
      const message = event.detail
      if (message.type === 'edit_operation_complete' && message.data) {
        this.handleEditOperationComplete(message.data)
      }
    })

    // Listen for alignment updates
    window.addEventListener('websocket-message-alignment', (event: any) => {
      const message = event.detail
      if (message.type === 'alignment_complete' && message.data) {
        this.handleAlignmentComplete(message.data)
      }
    })
  }

  /**
   * Handle witness update messages with version checking
   */
  public async handleWitnessUpdate(data: SyncMessage) {
    const { witness_id, chapter_id, book_id, version } = data
    
    try {
      const currentVersion = await this.getWitnessVersion(witness_id)
      
      if (version > currentVersion) {
        console.log(`🔄 Syncing witness ${witness_id}: v${currentVersion} → v${version}`)
        
        // Force refresh from IndexedDB
        await this.refreshWitnessState(witness_id, chapter_id, book_id)
        
        // Update version cache
        await this.updateWitnessVersion(witness_id, version)
        
        // Invalidate frontend caches
        this.invalidateFrontendCaches(witness_id)
      }
    } catch (error) {
      console.error('Failed to handle witness update:', error)
      this.queueRetry(witness_id)
    }
  }

  /**
   * Handle edit operation completion
   */
  private async handleEditOperationComplete(data: SyncMessage) {
    const { witness_id, chapter_id, book_id } = data
    
    try {
      // Force immediate refresh of witness state
      await this.refreshWitnessState(witness_id, chapter_id, book_id)
      
      // Update books store to trigger re-render
      const booksStore = useBooksStore.getState()
      // Refresh each witness to ensure consistency
      const chapterWitnesses = booksStore.witnesses.filter(w => w.chapter_id === chapter_id)
      await Promise.all(chapterWitnesses.map(w => booksStore.refreshWitness(w.id)))
      
      // Update segmentation store if active 
      const segmentationStore = useSegmentationStore.getState()
      if (segmentationStore.processingSession?.witnessId === witness_id) {
      await segmentationStore.refreshProcessingResults()
    }
      // Check if we need to refresh processing results
      
    } catch (error) {
      console.error('Failed to handle edit operation complete:', error)
      this.queueRetry(witness_id)
    }
  }

  /**
   * Handle alignment completion
   */
  private async handleAlignmentComplete(data: SyncMessage) {
    const { witness_id, chapter_id, book_id } = data
    
    try {
      // Refresh all witnesses in the chapter
      const booksStore = useBooksStore.getState()
      await booksStore.loadChapterData(book_id, chapter_id)
      
      // Force refresh of alignment viewer
      window.dispatchEvent(new CustomEvent('alignment-refreshed', {
        detail: { chapter_id, book_id }
      }))
      
    } catch (error) {
      console.error('Failed to handle alignment complete:', error)
    }
  }

  /**
   * Get current witness version from IndexedDB
   */
  private async getWitnessVersion(witness_id: string): Promise<number> {
    try {
      const cached = this.versionCache.get(witness_id)
      if (cached !== undefined) return cached

      const witness = await this.dbStorage.get('witnesses', witness_id)
      const version = witness?.data_integrity_hash ? 
        parseInt(witness.data_integrity_hash.slice(0, 8), 16) : 0
      
      this.versionCache.set(witness_id, version)
      return version
    } catch {
      return 0
    }
  }

  /**
   * Update witness version in cache and IndexedDB
   */
  private async updateWitnessVersion(witness_id: string, version: number): Promise<void> {
    this.versionCache.set(witness_id, version)
    
    try {
      const witness = await this.dbStorage.get('witnesses', witness_id)
      if (witness) {
        witness.last_modified = Date.now()
        await this.dbStorage.put('witnesses', witness)
      }
    } catch (error) {
      console.error('Failed to update witness version:', error)
    }
  }

  /**
   * Force refresh witness state from IndexedDB
   */
  private async refreshWitnessState(witness_id: string, chapter_id: string, book_id: string): Promise<void> {
    try {
      // Clear version cache to force refresh
      this.versionCache.delete(witness_id)
      
      // Refresh books store
      const booksStore = useBooksStore.getState()
      await booksStore.loadWitnesses(chapter_id)
      
      // Emit refresh event for components
      window.dispatchEvent(new CustomEvent('witness-state-refreshed', {
        detail: { witness_id, chapter_id, book_id }
      }))
      
    } catch (error) {
      console.error('Failed to refresh witness state:', error)
      throw error
    }
  }

  /**
   * Invalidate frontend caches for specific witness
   */
  private invalidateFrontendCaches(witness_id: string): void {
    // Clear React Query caches if using React Query
    // Clear component state caches
    window.dispatchEvent(new CustomEvent('witness-cache-invalidated', {
      detail: { witness_id }
    }))
  }

  /**
   * Queue retry for failed sync operations
   */
  private queueRetry(witness_id: string): void {
    const existing = this.retryQueue.find(item => item.witness_id === witness_id)
    if (!existing) {
      this.retryQueue.push({
        witness_id,
        retries: 0,
        timestamp: Date.now()
      })
    }
  }

  /**
   * Retry loop for failed sync operations
   */
  private startRetryLoop(): void {
    setInterval(async () => {
      const now = Date.now()
      const toRetry = this.retryQueue.filter(item => 
        item.retries < this.maxRetries && 
        now - item.timestamp >= this.retryDelay * Math.pow(2, item.retries)
      )

      for (const item of toRetry) {
        try {
          const witness = await this.dbStorage.get('witnesses', item.witness_id)
          if (witness) {
            await this.refreshWitnessState(item.witness_id, witness.chapter_id, witness.book_id)
            
            // Remove from retry queue
            this.retryQueue = this.retryQueue.filter(r => r.witness_id !== item.witness_id)
          }
        } catch (error) {
          item.retries++
          item.timestamp = now
          console.warn(`Retry ${item.retries} failed for witness ${item.witness_id}:`, error)
        }
      }
    }, 1000)
  }

  /**
   * Manually trigger sync for a witness
   */
  public async syncWitness(witness_id: string): Promise<void> {
    try {
      const witness = await this.dbStorage.get('witnesses', witness_id)
      if (witness) {
        await this.refreshWitnessState(witness_id, witness.chapter_id, witness.book_id)
      }
    } catch (error) {
      console.error('Manual sync failed:', error)
      throw error
    }
  }

  /**
   * Get sync status for debugging
   */
  public getSyncStatus(): {
    versionCache: Map<string, number>
    retryQueue: Array<{witness_id: string, retries: number, timestamp: number}>
  } {
    return {
      versionCache: new Map(this.versionCache),
      retryQueue: [...this.retryQueue]
    }
  }

}

// Export singleton instance
export const syncManager = new SyncManager()

// Hook for React components
export const useSyncManager = () => syncManager